/*
  TIG 24 Sep 2026. Waist + Length pickers over HERA's single combined size option.

  The product data is untouched: variants stay "10R". This element mirrors the real
  radios (PDP) or the quick-buy labels, so VariantRadios, sold-out marks, Klaviyo back
  in stock, Rebuy, Swish and analytics all behave as they did.

  Rules:
  - nothing is preselected;
  - choosing one side marks combinations that are sold out on the other side;
  - a sold-out combination can still be selected, so the theme can show SOLD OUT and
    the back-in-stock option;
  - the underlying radio only changes once both sides are chosen.
*/
class HeraWaistLength extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.mode = this.dataset.mode === 'quickbuy' ? 'quickbuy' : 'pdp';
    this.noteEl = this.querySelector('[data-wl-note]');
    this.waistInputs = [...this.querySelectorAll('.hera-wl__group--waist .hera-wl__input')];
    this.lengthInputs = [...this.querySelectorAll('.hera-wl__group--length .hera-wl__input')];

    if (!this.collectTargets()) {
      // Nothing to mirror: leave the original picker visible and bow out.
      this.remove();
      return;
    }

    this.hideOriginal();
    this.addEventListener('change', (e) => {
      if (!e.target.classList.contains('hera-wl__input')) return;
      this.onPick();
    });
    const guide = this.querySelector('[data-wl-guide]');
    if (guide) guide.addEventListener('click', this.openGuide.bind(this));

    this.syncFromTheme();
    this.paint();
  }

  // Build [{ title, waist, length, available, select() }] from whatever the page has.
  collectTargets() {
    this.targets = [];
    if (this.mode === 'pdp') {
      const radios = this.variantRadios();
      if (!radios) return false;
      this.original = radios.querySelector('fieldset.product-form__input-grid:not(.hera-wl__group)');
      radios.querySelectorAll('fieldset.product-form__input-grid input[type="radio"]').forEach((input) => {
        const parsed = this.parse(input.value);
        if (!parsed) return;
        this.targets.push({
          ...parsed,
          input,
          available: !input.classList.contains('out-of-stock'),
          isChecked: () => input.checked,
          select: () => {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          },
        });
      });
    } else {
      const modal = this.closest('mobile-quickbuy-modal') || this.parentElement;
      this.original = modal && modal.querySelector('fieldset:not(.hera-wl__group)');
      const labels = modal ? modal.querySelectorAll('.variant-selector-label') : [];
      labels.forEach((label) => {
        const input = label.querySelector('.variant-selector-input');
        if (!input) return;
        const parsed = this.parse(input.getAttribute('value'));
        if (!parsed) return;
        this.targets.push({
          ...parsed,
          input,
          available: !input.disabled && !label.classList.contains('disabled'),
          isChecked: () => label.classList.contains('checked'),
          select: () => label.click(),
        });
      });
    }
    return this.targets.length > 0;
  }

  variantRadios() {
    const section = this.dataset.section;
    return (
      (section && document.querySelector(`variant-radios[data-section="${section}"]`)) ||
      this.parentElement.querySelector('variant-radios') ||
      (this.closest('.product__info-container') || document).querySelector('variant-radios')
    );
  }

  parse(title) {
    const m = /^(\d+)\s*([SRL])$/.exec((title || '').trim());
    return m ? { title: title.trim(), waist: m[1], length: m[2] } : null;
  }

  hideOriginal() {
    if (this.original) this.original.classList.add('hera-wl-hidden');
  }

  // Pick up a variant already selected by the URL (?variant=) or by the theme.
  syncFromTheme() {
    const chosen = this.targets.find((t) => t.isChecked());
    if (!chosen) return;
    this.setInput(this.waistInputs, chosen.waist);
    this.setInput(this.lengthInputs, chosen.length);
  }

  setInput(list, value) {
    const hit = list.find((i) => i.value === value);
    if (hit) hit.checked = true;
  }

  get waist() {
    const i = this.waistInputs.find((x) => x.checked);
    return i ? i.value : null;
  }

  get length() {
    const i = this.lengthInputs.find((x) => x.checked);
    return i ? i.value : null;
  }

  onPick() {
    this.paint();
    const target = this.targets.find((t) => t.waist === this.waist && t.length === this.length);
    if (target) target.select();
  }

  label(code) {
    return { S: 'Short', R: 'Regular', L: 'Long' }[code] || code;
  }

  paint() {
    const waist = this.waist;
    const length = this.length;

    // Cross out what cannot be bought, given the other side's choice.
    this.waistInputs.forEach((input) => {
      const pool = this.targets.filter((t) => t.waist === input.value && (!length || t.length === length));
      input.classList.toggle('out-of-stock', pool.length > 0 && !pool.some((t) => t.available));
    });
    this.lengthInputs.forEach((input) => {
      const pool = this.targets.filter((t) => t.length === input.value && (!waist || t.waist === waist));
      input.classList.toggle('out-of-stock', pool.length > 0 && !pool.some((t) => t.available));
    });

    const waistValue = this.querySelector('[data-wl-value="waist"]');
    const lengthValue = this.querySelector('[data-wl-value="length"]');
    if (waistValue) waistValue.textContent = waist || 'Select';
    if (lengthValue) lengthValue.textContent = length ? this.label(length) : 'Select';

    if (!this.noteEl) return;
    const chosen = waist && length ? this.targets.find((t) => t.waist === waist && t.length === length) : null;
    if (chosen && !chosen.available) {
      // no nudge towards a different length: sending someone to the wrong size buys a return
      this.soldOutNote(`UK size ${waist} ${this.label(length)} is sold out.`);
      this.noteEl.hidden = false;
    } else {
      this.noteEl.hidden = true;
    }
  }

  // The back in stock button comes from an app and does not always render, so only point
  // at it once it is actually on the page. Checked again shortly after, since the app
  // script can land later than this.
  soldOutNote(base) {
    const pointer = ' Use notify me when available below.';
    const visible = () => [...document.querySelectorAll('button, a, input[type="submit"]')].some((el) => {
      if (!el.offsetParent) return false;
      const text = (el.textContent || el.value || '').trim();
      return /notify me|back in stock|email me when/i.test(text);
    });
    this.noteEl.textContent = base + (visible() ? pointer : '');
    clearTimeout(this._noteTimer);
    this._noteTimer = setTimeout(() => {
      if (!this.noteEl.hidden && this.noteEl.textContent.startsWith(base) && visible()) {
        this.noteEl.textContent = base + pointer;
      }
    }, 1500);
  }

  openGuide(e) {
    e.preventDefault();
    const button = document.querySelector('size-guide .size-guide-button, .size-guide-button');
    if (button) button.click();
  }
}

if (!customElements.get('hera-waist-length')) {
  customElements.define('hera-waist-length', HeraWaistLength);
}
